/**
 * Recently-viewed properties — pure localStorage, with optional silent
 * cross-device sync for authenticated users.
 *
 *   addView(property)       → prepends to local history (dedup, max 10) +
 *                             fire-and-forget POST to server if logged in
 *   getViews()              → returns [{id, snapshot, viewedAt}, …]
 *   clearViews()            → wipes the local list + best-effort DELETE on server
 *   syncWithServer({get,    → pulls server views and merges into local
 *                   apiBase, token, propertiesApi })
 *
 * The `snapshot` is a tiny subset (title, price, type, area, county, cover image,
 * bed, bath) so the rail renders instantly without an API call. We still refresh
 * each card from the live API on mount so prices stay accurate.
 */
import axios from 'axios';

const KEY = 'lh:recent-views:v1';
const MAX = 10;
const SERVER_MAX = 50; // matches recent_views_routes.MAX_VIEWS

const safeParse = (raw) => {
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_e) {
    return [];
  }
};

const _getToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('token');
  } catch (_e) {
    return null;
  }
};

const _apiBase = () => process.env.REACT_APP_BACKEND_URL || '';

/** Best-effort POST — never throws, never blocks the caller. */
const _postViews = (views) => {
  const token = _getToken();
  if (!token || !views.length) return;
  try {
    axios.post(
      `${_apiBase()}/api/users/me/recent-views`,
      {
        views: views.map((v) => ({
          id: String(v.id),
          viewed_at: v.viewedAt || Date.now(),
          ...(v.snapshot?.priceAtView != null
            ? { price_at_view: Number(v.snapshot.priceAtView) }
            : {}),
        })),
      },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 6000 }
    ).catch(() => { /* silent */ });
  } catch (_e) {
    /* silent */
  }
};

export const getViews = () => {
  if (typeof window === 'undefined') return [];
  try {
    return safeParse(window.localStorage.getItem(KEY) || '[]');
  } catch (_e) {
    return [];
  }
};

const _writeLocal = (views) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(views.slice(0, MAX)));
  } catch (_e) {
    /* quota — fail silently */
  }
};

export const addView = (property) => {
  if (typeof window === 'undefined' || !property) return;
  const id = property._id || property.id;
  if (!id) return;
  const snapshot = {
    id: String(id),
    title: property.title,
    price: property.price,
    priceAtView: property.price, // immutable — what the user actually saw
    type: property.type,
    propertyType: property.propertyType,
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    image: (property.images && property.images[0]) || null,
    area: property.location?.area,
    county: property.location?.county || property.location?.city,
    ber: property.ber,
  };
  const entry = { id: snapshot.id, snapshot, viewedAt: Date.now() };
  const current = getViews().filter((v) => v.id !== snapshot.id);
  current.unshift(entry);
  _writeLocal(current);
  _postViews([entry]); // silent cross-device sync
};

export const clearViews = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch (_e) {
    /* ignore */
  }
  const token = _getToken();
  if (!token) return;
  try {
    axios.delete(`${_apiBase()}/api/users/me/recent-views`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 6000,
    }).catch(() => { /* silent */ });
  } catch (_e) {
    /* silent */
  }
};

/**
 * Pull the user's server-side view ids (set on other devices) and merge them
 * into local. For ids we've never seen, we don't have a snapshot — we mark
 * them as `{id, snapshot: {id}, viewedAt}` and let the rail hydrate them via
 * the existing `propertiesAPI.getById` background-refresh on mount.
 *
 * Caps the merged result at the local MAX. Idempotent / safe to call on every
 * mount.
 */
export const syncWithServer = async () => {
  const token = _getToken();
  if (!token) return getViews();
  let serverViews = [];
  try {
    const res = await axios.get(`${_apiBase()}/api/users/me/recent-views`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 6000,
    });
    serverViews = (res.data && res.data.views) || [];
  } catch (_e) {
    return getViews(); // network/auth failure → don't disrupt the local list
  }

  // Merge: dedupe by id, keep newest `viewed_at` per id.
  const local = getViews();
  const byId = new Map();
  for (const v of local) byId.set(v.id, v);
  for (const sv of serverViews) {
    const id = String(sv.id);
    const ts = Number(sv.viewed_at) || 0;
    const pav = sv.price_at_view != null ? Number(sv.price_at_view) : undefined;
    const existing = byId.get(id);
    if (!existing) {
      // Only id known — snapshot will be hydrated by the rail.
      byId.set(id, {
        id,
        snapshot: { id, ...(pav != null ? { priceAtView: pav } : {}) },
        viewedAt: ts,
      });
    } else if (ts > (existing.viewedAt || 0)) {
      byId.set(id, {
        ...existing,
        viewedAt: ts,
        snapshot: {
          ...existing.snapshot,
          ...(pav != null && existing.snapshot?.priceAtView == null
            ? { priceAtView: pav }
            : {}),
        },
      });
    }
  }
  const merged = Array.from(byId.values())
    .sort((a, b) => (b.viewedAt || 0) - (a.viewedAt || 0))
    .slice(0, SERVER_MAX); // local cap applied later by _writeLocal
  _writeLocal(merged);
  return merged.slice(0, MAX);
};

export default { getViews, addView, clearViews, syncWithServer };
